/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import { useEffect } from 'react'
import { WidgetGuard } from 'features/types'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { getMetricCategory } from 'utils/MetricItemUtil'
import { createMetric } from 'utils/runSearchUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { getOptions } from 'utils/timelineUtil'
import { toFilter } from 'utils/widgetUtil'
import { InsightViewOption, MeasureType } from 'enums'
import { AudienceTimelineWidget } from 'schemas/AudienceTimelineWidgetSchema'
import { Dashboard } from 'schemas/DashboardSchema'
import { MetricOptions } from 'features/widget/types'
import { useAppDispatch } from 'app/hooks'
import { getTimelineAnnotationsByTopicId } from 'features/topicAnnotations/asyncThunks'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'
import { getChildTypes } from '../utils'

const createSearches = (
  widgetSetting: AudienceTimelineWidget,
  dashboard: Dashboard,
  pageConfigUser: any
) => {
  const { measureTypes, viewOption, precision, selectedTopic } = widgetSetting

  const option: MetricOptions = {}

  if (measureTypes!.length === 1) {
    option.metricCategory = getMetricCategory(viewOption)
  }

  option.isAudienceQuery = true

  const expandedMeasureTypes = [...measureTypes!]
  if (viewOption === InsightViewOption.OVERVIEW && measureTypes!.length === 1) {
    expandedMeasureTypes.push(
      ...measureTypes!.flatMap((measureType) => getChildTypes(viewOption, measureType!))
    )
  }

  if (!measureTypes!.includes(MeasureType.MENTIONS)) {
    expandedMeasureTypes.push(MeasureType.MENTIONS)
  }

  return [
    createMetric(
      dashboard.id!,
      dashboard.name!,
      precision!,
      [selectedTopic.topicId!],
      toFilter(widgetSetting, pageConfigUser.timeZone),
      expandedMeasureTypes.flatMap((measure) => getMetricSeries(measure!)),
      getOptions(widgetSetting),
      option
    )
  ]
}

const RunSearchEffect: WidgetGuard = ({ widgetId, children }) => {
  const widgetSetting = useDashboardWidgetSetting<AudienceTimelineWidget>(widgetId)
  const dashboard = useDashboardData()
  const pageConfigUser = usePageConfigUser()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboard!, pageConfigUser)

    if (searches.length > 0) {
      searches.sort((a, b) => a.priority - b.priority)
      dispatch(getWidgetForDashboard({ searches, widgetId, timestamp: refreshTimestamp }))
    }
  }, [widgetId, dispatch, refreshTimestamp, widgetSetting, dashboard, pageConfigUser])

  useEffect(() => {
    dispatch(getTimelineAnnotationsByTopicId({ topicId: widgetSetting.topicInfos![0].topicId! }))
  }, [widgetSetting, dispatch])

  return children
}

export default RunSearchEffect
